/**
 * Demo engine — a small generative music box built on Web Audio.
 *
 * The transport is a {@link MusicalClock} anchored to the audio context, not
 * a buffer being played: seeking re-anchors the clock, which is why the record
 * can be scratched many times a second, and why the pitch fader changes tempo
 * *and* pitch the way a real turntable does.
 */

import { clamp } from '../../lib/math.ts';
import type { DemoTrack } from '../../types/demo.ts';
import type { Engine } from '../engine.ts';
import { LOOKAHEAD, stepDuration, TICK } from './arrangement.ts';
import { MusicalClock } from './clock.ts';
import { DemoAudioHost } from './host.ts';
import { emitStep } from './scheduler.ts';

export class DemoEngine extends DemoAudioHost implements Engine {
  track: DemoTrack | null = null;

  readonly #clock = new MusicalClock();
  /** Musical position already handed to the scheduler. */
  #scheduledTo = 0;
  #timer: ReturnType<typeof setInterval> | null = null;

  override get playing(): boolean {
    return this.#clock.running;
  }

  get rate(): number {
    return this.#clock.rate;
  }

  get duration(): number {
    return this.track?.duration ?? 0;
  }

  get position(): number {
    return this.#clock.position(this.now);
  }

  load(track: DemoTrack | null | undefined): void {
    this.track = track ?? null;
    this.#clock.seek(0, this.now);
    this.#scheduledTo = 0;
    this.emit('durationchange', this.duration);
  }

  async play(): Promise<void> {
    if (!this.track || this.playing) return;
    await this.ensure();
    this.#clock.start(this.now);
    this.#scheduledTo = Math.max(this.position, 0);
    this.setCrackle(this.crackleOn);
    this.#startTimer();
    this.#schedule();
    this.emit('play');
  }

  pause(): void {
    if (!this.playing) return;
    this.#clock.stop(this.now);
    this.#stopTimer();
    this.fadeCrackle(0, 0.12);
    /* The tail is left to ring out rather than being cut dead. */
    this.emit('pause');
  }

  stop(): void {
    this.pause();
    this.seek(0);
  }

  seek(sec: number): void {
    const p = clamp(sec, 0, this.duration);
    this.#clock.seek(p, this.now);
    this.#scheduledTo = p;
    this.emit('timeupdate', p);
  }

  /** Turntable pitch: changes tempo and pitch together. */
  setRate(r: number): number {
    this.#clock.setRate(clamp(r, 0.5, 2), this.now);
    return this.#clock.rate;
  }

  override destroy(): void {
    this.#stopTimer();
    super.destroy();
  }

  #startTimer(): void {
    this.#stopTimer();
    this.#timer = setInterval(() => {
      this.#schedule();
    }, TICK);
  }

  #stopTimer(): void {
    if (this.#timer !== null) clearInterval(this.#timer);
    this.#timer = null;
  }

  /** Render every step that falls inside the lookahead window. */
  #schedule(): void {
    const graph = this.graph;
    const track = this.track;
    if (!this.playing || !track || !graph) return;

    const pos = this.position;
    if (pos >= track.duration) {
      this.#endOfSide(track.duration);
      return;
    }

    const stepDur = stepDuration(track.bpm);
    const horizon = graph.ctx.currentTime + LOOKAHEAD;
    if (this.#scheduledTo < pos) this.#scheduledTo = pos;

    let guard = 0;
    while (this.#clock.timeOf(this.#scheduledTo) < horizon && guard++ < 128) {
      const step = Math.round(this.#scheduledTo / stepDur);
      emitStep(step, {
        bus: graph,
        track,
        when: this.#clock.timeOf(step * stepDur),
        stepSeconds: stepDur / this.rate,
        rate: this.rate,
      });
      this.#scheduledTo = (step + 1) * stepDur;
    }
    this.emit('timeupdate', pos);
  }

  #endOfSide(duration: number): void {
    this.#clock.stop(this.now);
    this.#clock.seek(duration, this.now);
    this.#stopTimer();
    this.setCrackle(false);
    this.emit('ended');
  }
}
